/* eslint-disable react-hooks/exhaustive-deps */
import { FormEvent, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StatusCode } from '../../api/enumerations';
import { login } from '../../api/login';
import {
  EmailIcon,
  IconVisibility,
  PasswordIcon,
} from '../../components/InputIcon';
import Snackbar from '../../components/Snackbar';
import { WhiteCircularProgress } from '../../components/UI/Typography';
import { Constants } from '../../constants/login';
import { GlobalContext } from '../../context/global';
import { getCompanySubdomain } from '../../helpers';
import {
  getLocalStorageToken,
  setLocalStorageToken,
} from '../../helpers/localStorage';
import useErrorMessage from '../../hooks/useErrorMessage';
import {
  ContainerGrid,
  InputBox,
  InputTitle,
  LoginGrid,
  LogoGrid,
  LogoImg,
  StyledButtonBase,
  StyledTextField,
  SubmitButton,
} from './styles';

export function Login({ logo }: { logo: string }): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const companySubdomain = getCompanySubdomain();

  const { openSnackbar, setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);

  const { getErrorMessage } = useErrorMessage();

  const navigate = useNavigate();
  const token = getLocalStorageToken();

  useEffect(() => {
    if (token !== null && token !== 'undefined') {
      navigate('/home');
    }
  }, [navigate, token]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimEmail = email.trim();

    try {
      setSubmitLoading(true);
      const response = await login({
        email: trimEmail,
        password,
        companySubdomain,
      });

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (
        response.detail.status_code !== StatusCode.OK ||
        !response.access_token ||
        !response.user
      ) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      const item = response.access_token;
      setLocalStorageToken(item);
      window.dispatchEvent(new Event('token-set'));
      navigate('/home');
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setErrorMessage(true);
      setOpenSnackbar(true);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <ContainerGrid container>
      <LogoGrid item xs={8}>
        <LogoImg>
          <img src={logo} alt="Logo" />
        </LogoImg>
      </LogoGrid>
      <LoginGrid item xs={4}>
        <InputBox component="form" id="login" onSubmit={handleSubmit}>
          <InputTitle>{Constants.title}</InputTitle>
          <StyledTextField
            required
            id="email"
            variant="standard"
            InputProps={EmailIcon}
            placeholder={Constants.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <StyledTextField
            required
            type={showPassword ? 'text' : 'password'}
            variant="standard"
            InputProps={{
              startAdornment: <PasswordIcon />,
              endAdornment: (
                <IconVisibility
                  showPassword={showPassword}
                  setShowPassword={setShowPassword}
                />
              ),
            }}
            id="password"
            placeholder={Constants.passwordPlaceholder}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <SubmitButton form="login" type="submit" disabled={submitLoading}>
            {submitLoading ? (
              <WhiteCircularProgress size={22} />
            ) : (
              Constants.submit
            )}
          </SubmitButton>
          <StyledButtonBase
            disableTouchRipple
            onClick={() => navigate('/forgot-password')}
          >
            {Constants.forgotPassword}
          </StyledButtonBase>
        </InputBox>
      </LoginGrid>
      {openSnackbar && <Snackbar />}
    </ContainerGrid>
  );
}
